/**
 * Технические наблюдения о браузере (`s698` → `s704`) — только то, что обычный
 * браузер штатно отдаёт любой странице.
 *
 * ⛔ ЧЕГО ЗДЕСЬ НЕТ И БЫТЬ НЕ ДОЛЖНО: отпечатков через canvas и звук (ничего не
 * рисуется и не считывается), перебора шрифтов, WebRTC, чтения буфера, клавиш и
 * движений мыши, запросов доступа к камере, микрофону и местоположению. Сервер
 * принимает профиль по закрытому перечню полей — чего в перечне нет, то не
 * сохранится, даже если кто-то допишет сюда лишнее.
 *
 * ⛔ ДОПОЛНИТЕЛЬНЫЕ СВЕДЕНИЯ (`s704`, продолжение 05.10.2026) — графика, питание,
 * хранилище сайта, разрешения, устройства связи. У каждой группы своё состояние
 * `st_*`: получено / не поддерживается / не разрешено / ошибка. Состояния разрешений
 * ЧИТАЮТСЯ, доступ не запрашивается; названия устройств связи браузер отдаёт сам и
 * только если доступ к микрофону уже выдан для звонков. Местоположение спрашивает
 * отдельный модуль (`geolokaciyaVhoda.ts`) и только когда сервер ответил «да».
 *
 * ⛔ СБОЙ НАБЛЮДЕНИЯ НЕ ИМЕЕТ ПРАВА МЕШАТЬ РАБОТЕ: каждое чтение обёрнуто, каждый
 * запрос молчит об ошибке. Вход и панель живут и без него.
 *
 * «Устройство» — случайный номер НАШЕГО приложения в хранилище этого браузера.
 * Это браузер, а не компьютер: другой браузер или очистка данных дадут новый
 * номер, и восстанавливать прежний мы не пытаемся.
 */

const KLYUCH = 'opsmind_browser_observation';

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.opsmind.ru/api/v1';

const ROOT = API_BASE.replace(/\/api\/v1\/?$/, '');

let browserId: string | undefined;

type UaData = {
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
};

type Soedinenie = { type?: string; effectiveType?: string; downlink?: number; downlinkMax?: number; rtt?: number; saveData?: boolean };

type Batareya = { level?: number; charging?: boolean; chargingTime?: number; dischargingTime?: number };

type Sostoyanie = 'ok' | 'unsupported' | 'denied' | 'error';

/** Ни одно чтение не держит отправку профиля дольше этого. */
const ZHDAT_MS = 3000;

/** Обещание с пределом ожидания: зависший ответ браузера — это «ошибка», а не вечное ожидание. */
function vSrok<T>(p: Promise<T>): Promise<T> {
  return Promise.race([p, new Promise<T>((_, otkaz) => setTimeout(() => otkaz(new Error('timeout')), ZHDAT_MS))]);
}

/** Чем кончилось чтение: запрет браузера или страницы — «не разрешено», прочее — «ошибка». */
function poOshibke(e: unknown): Sostoyanie {
  const imya = (e as { name?: string } | null)?.name;
  return imya === 'NotAllowedError' || imya === 'SecurityError' ? 'denied' : 'error';
}

const konechnoe = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : undefined);
const mediya = (z: string): boolean => matchMedia(z).matches;

/**
 * Графика: две строки драйвера, которые браузер сам отдаёт странице. Картинка не
 * рисуется и не считывается — это не отпечаток холста. Контекст сразу отпускается.
 */
function grafika(): Record<string, unknown> {
  try {
    const holst = document.createElement('canvas');
    const gl = (holst.getContext('webgl') ?? holst.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return { st_gpu: 'unsupported' };
    const bezMaski = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = gl.getParameter(bezMaski ? bezMaski.UNMASKED_VENDOR_WEBGL : gl.VENDOR);
    const adapter = gl.getParameter(bezMaski ? bezMaski.UNMASKED_RENDERER_WEBGL : gl.RENDERER);
    proba(() => gl.getExtension('WEBGL_lose_context')?.loseContext());
    return {
      st_gpu: 'ok',
      gpu_vendor: typeof vendor === 'string' ? vendor : undefined,
      gpu_renderer: typeof adapter === 'string' ? adapter : undefined,
      gpu_source: bezMaski ? 'webgl_debug' : 'webgl',
    };
  } catch (e) {
    return { st_gpu: poOshibke(e) };
  }
}

async function pitanie(nav: Navigator): Promise<Record<string, unknown>> {
  const sprosit = (nav as Navigator & { getBattery?: () => Promise<Batareya> }).getBattery;
  if (typeof sprosit !== 'function') return { st_battery: 'unsupported' };
  try {
    const b = await vSrok(sprosit.call(nav));
    return {
      st_battery: 'ok',
      battery_level_pct: typeof b.level === 'number' ? Math.round(b.level * 100) : undefined,
      battery_charging: typeof b.charging === 'boolean' ? b.charging : undefined,
      // «Бесконечность» браузера значит «не знаю» — поля тогда просто нет.
      battery_charging_time_s: konechnoe(b.chargingTime),
      battery_discharging_time_s: konechnoe(b.dischargingTime),
    };
  } catch (e) {
    return { st_battery: poOshibke(e) };
  }
}

/** Место, которое браузер отвёл НАШЕМУ сайту. Это не свободный диск компьютера. */
async function hranilishche(nav: Navigator): Promise<Record<string, unknown>> {
  if (typeof nav.storage?.estimate !== 'function') return { st_storage: 'unsupported' };
  try {
    const o = await vSrok(nav.storage.estimate());
    const mb = (v: unknown) => (konechnoe(v) === undefined ? undefined : Math.round(((v as number) / 1048576) * 100) / 100);
    const zakrepleno = typeof nav.storage.persisted === 'function' ? await vSrok(nav.storage.persisted()).catch(() => undefined) : undefined;
    return { st_storage: 'ok', storage_usage_mb: mb(o.usage), storage_quota_mb: mb(o.quota), storage_persisted: zakrepleno };
  } catch (e) {
    return { st_storage: poOshibke(e) };
  }
}

/** Состояние одного разрешения. Только чтение: `query` запрос доступа не показывает. */
export async function sostoyanieRazresheniya(imya: string): Promise<string> {
  try {
    if (typeof navigator.permissions?.query !== 'function') return 'unsupported';
    const o = await vSrok(navigator.permissions.query({ name: imya as PermissionName }));
    return ['granted', 'denied', 'prompt'].includes(o.state) ? o.state : 'error';
  } catch (e) {
    // Имя, которого браузер не знает, — `TypeError`: он этого просто не сообщает.
    return (e as { name?: string } | null)?.name === 'TypeError' ? 'unsupported' : 'error';
  }
}

async function razresheniya(): Promise<Record<string, unknown>> {
  if (typeof navigator.permissions?.query !== 'function') {
    // Без общего чтения об уведомлениях браузер всё же говорит сам.
    const u = proba(() => (typeof Notification === 'undefined' ? undefined : Notification.permission));
    return { st_permissions: 'unsupported', perm_notifications: u === 'default' ? 'prompt' : u };
  }
  const [geo, uved, mik, kam] = await Promise.all(['geolocation', 'notifications', 'microphone', 'camera'].map(sostoyanieRazresheniya));
  return { st_permissions: 'ok', perm_geolocation: geo, perm_notifications: uved, perm_microphone: mik, perm_camera: kam };
}

/**
 * Устройства связи: что браузер показывает сам. Камера и микрофон НЕ включаются и
 * доступ не запрашивается: без выданного разрешения браузер отдаёт лишь наличие
 * (названия пустые), с разрешением, выданным для звонков, — и названия.
 */
async function ustroystvaSvyazi(nav: Navigator): Promise<Record<string, unknown>> {
  if (typeof nav.mediaDevices?.enumerateDevices !== 'function') return { st_media: 'unsupported' };
  // В фоновой вкладке браузер список не отдаёт: пустой ответ был бы «устройств нет».
  if (document.visibilityState !== 'visible') return { st_media: 'error' };
  try {
    const spisok = await vSrok(nav.mediaDevices.enumerateDevices());
    const skolko = (vid: string) => spisok.filter((d) => d.kind === vid).length;
    const nazvaniya = [...new Set(spisok.map((d) => d.label).filter(Boolean))].slice(0, 12);
    return {
      st_media: 'ok',
      media_audio_in: skolko('audioinput'),
      media_audio_out: skolko('audiooutput'),
      media_video_in: skolko('videoinput'),
      media_labels: nazvaniya.length ? nazvaniya : undefined,
    };
  } catch (e) {
    return { st_media: poOshibke(e) };
  }
}

/** Остальное об экране, вводе и соединении — простые чтения, каждое в своей обёртке. */
function ekranIVvod(nav: Navigator & { connection?: Soedinenie; pdfViewerEnabled?: boolean }): Record<string, unknown> {
  return {
    pixel_depth: proba(() => screen.pixelDepth),
    outer_width: proba(() => window.outerWidth),
    outer_height: proba(() => window.outerHeight),
    orientation_angle: proba(() => screen.orientation?.angle),
    screen_extended: proba(() => (screen as Screen & { isExtended?: boolean }).isExtended),
    any_pointer_fine: proba(() => mediya('(any-pointer: fine)')),
    any_pointer_coarse: proba(() => mediya('(any-pointer: coarse)')),
    any_hover: proba(() => mediya('(any-hover: hover)')),
    color_gamut: proba(() => ['rec2020', 'p3', 'srgb'].find((g) => mediya(`(color-gamut: ${g})`))),
    hdr: proba(() => mediya('(dynamic-range: high)')),
    color_scheme: proba(() => (mediya('(prefers-color-scheme: dark)') ? 'dark' : 'light')),
    reduced_motion: proba(() => mediya('(prefers-reduced-motion: reduce)')),
    forced_colors: proba(() => mediya('(forced-colors: active)')),
    contrast: proba(() => ['more', 'less', 'custom', 'no-preference'].find((k) => mediya(`(prefers-contrast: ${k})`))),
    pdf_viewer: proba(() => nav.pdfViewerEnabled),
    conn_downlink_max_mbps: proba(() => konechnoe(nav.connection?.downlinkMax)),
  };
}

/**
 * Когда человек последний раз что-то нажал или прокрутил в панели (`s704`, дополнение 4:
 * «сколько прошло с последней активности на экране»). Хранится только момент —
 * что именно сделано, не запоминается и никуда не уходит.
 */
let posledneeDeystvie: number | null = null;

/** Слой ходьбы сообщает о настоящем (isTrusted) действии человека. */
export function otmetitDeystvie(): void {
  posledneeDeystvie = performance.now();
}

/** Давность последнего действия в секундах; до первого действия на странице — неизвестна. */
function davnostDeystviya(): Record<string, string> {
  if (posledneeDeystvie === null) return {};
  const sek = Math.round((performance.now() - posledneeDeystvie) / 1000);
  return sek >= 0 && sek <= 604800 ? { 'X-Opsmind-Idle': String(sek) } : {};
}

/** Заголовки наблюдения — на каждый запрос к нашему серверу: номер браузера и давность действия. */
export function browserHeaders(): Record<string, string> {
  try {
    if (!browserId) {
      const saved = localStorage.getItem(KLYUCH);
      browserId = saved && /^[0-9a-f-]{36}$/i.test(saved) ? saved : crypto.randomUUID();
      localStorage.setItem(KLYUCH, browserId);
    }
    return { 'X-Opsmind-Browser': browserId, ...davnostDeystviya() };
  } catch {
    // Хранилище закрыто (приватный режим, запрет) — номера нет, работа продолжается.
    return davnostDeystviya();
  }
}

function proba<T>(f: () => T): T | undefined {
  try {
    return f();
  } catch {
    return undefined;
  }
}

/** Откуда пришли: сайт и путь. Параметры и якорь отрезаются здесь же, до отправки. */
function istochnik(): string | undefined {
  const ref = proba(() => document.referrer);
  if (!ref) return undefined;
  const u = proba(() => new URL(ref));
  return u ? `${u.origin}${u.pathname}` : undefined;
}

function izAgenta(ua: string) {
  const versiya = /(?:Edg|OPR|YaBrowser|Firefox|Chrome|Version)\/([\d.]+)/.exec(ua);
  const os = /Windows NT ([\d.]+)|Android ([\d.]+)|(?:iPhone OS|CPU OS) ([\d_]+)|Mac OS X ([\d_.]+)/.exec(ua);
  return {
    browser_name: /Edg\//.test(ua) ? 'Edge'
      : /YaBrowser\//.test(ua) ? 'Яндекс Браузер'
      : /OPR\//.test(ua) ? 'Opera'
      : /Firefox\//.test(ua) ? 'Firefox'
      : /Chrome\//.test(ua) ? 'Chrome'
      : /Safari\//.test(ua) ? 'Safari' : undefined,
    browser_version: versiya?.[1],
    os_name: /Windows/.test(ua) ? 'Windows'
      : /Android/.test(ua) ? 'Android'
      : /iPhone|iPad/.test(ua) ? 'iOS'
      : /Mac OS/.test(ua) ? 'macOS'
      : /Linux/.test(ua) ? 'Linux' : undefined,
    os_version: os?.slice(1).find(Boolean)?.replaceAll('_', '.'),
    device_type: /iPad|Tablet/.test(ua) ? 'tablet' : /Mobile/.test(ua) ? 'mobile' : 'desktop',
  };
}

/** Собрать профиль. Чего браузер не даёт — того в профиле просто нет. */
async function sobratProfil(): Promise<Record<string, unknown>> {
  const nav = navigator as Navigator & { deviceMemory?: number; connection?: Soedinenie; userAgentData?: UaData; pdfViewerEnabled?: boolean };
  const profil: Record<string, unknown> = {
    ...izAgenta(nav.userAgent),
    language: nav.language,
    languages: proba(() => [...nav.languages]),
    locale: proba(() => Intl.DateTimeFormat().resolvedOptions().locale),
    timezone: proba(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    tz_offset_min: proba(() => -new Date().getTimezoneOffset()),
    platform: proba(() => nav.platform),
    vendor: proba(() => nav.vendor),
    screen_width: proba(() => screen.width),
    screen_height: proba(() => screen.height),
    avail_width: proba(() => screen.availWidth),
    avail_height: proba(() => screen.availHeight),
    window_width: proba(() => window.innerWidth),
    window_height: proba(() => window.innerHeight),
    dpr: proba(() => window.devicePixelRatio),
    color_depth: proba(() => screen.colorDepth),
    orientation: proba(() => screen.orientation?.type),
    touch_points: proba(() => nav.maxTouchPoints),
    pointer: proba(() => (matchMedia('(pointer: fine)').matches ? 'fine' : matchMedia('(pointer: coarse)').matches ? 'coarse' : 'none')),
    hover: proba(() => matchMedia('(hover: hover)').matches),
    hardware_concurrency: proba(() => nav.hardwareConcurrency),
    device_memory_gb: proba(() => nav.deviceMemory),
    conn_type: proba(() => nav.connection?.type),
    conn_effective_type: proba(() => nav.connection?.effectiveType),
    conn_downlink_mbps: proba(() => nav.connection?.downlink),
    conn_rtt_ms: proba(() => nav.connection?.rtt),
    conn_save_data: proba(() => nav.connection?.saveData),
    online: proba(() => nav.onLine),
    cookies_enabled: proba(() => nav.cookieEnabled),
    local_storage: proba(() => { localStorage.setItem('__op', '1'); localStorage.removeItem('__op'); return true; }) ?? false,
    session_storage: proba(() => { sessionStorage.setItem('__op', '1'); sessionStorage.removeItem('__op'); return true; }) ?? false,
    display_mode: proba(() => (['standalone', 'fullscreen', 'minimal-ui'].find((m) => matchMedia(`(display-mode: ${m})`).matches) ?? 'browser')),
    webdriver: proba(() => nav.webdriver === true),
    referrer: istochnik(),
    ...proba(() => ekranIVvod(nav)),
    ...grafika(),
  };

  // Группы, которые браузер отдаёт не сразу. Сбой одной остальным не мешает:
  // каждая возвращает своё состояние, а не бросает ошибку.
  const gruppy = await Promise.all([
    pitanie(nav).catch(() => ({ st_battery: 'error' })),
    hranilishche(nav).catch(() => ({ st_storage: 'error' })),
    razresheniya().catch(() => ({ st_permissions: 'error' })),
    ustroystvaSvyazi(nav).catch(() => ({ st_media: 'error' })),
  ]);
  Object.assign(profil, ...gruppy);

  // Подсказки браузера о себе: есть только в Chromium. Браузер вправе не ответить.
  const ua = nav.userAgentData;
  if (ua) {
    profil.ua_brands = proba(() => ua.brands?.map((b) => `${b.brand} ${b.version}`).join(', '));
    profil.ua_mobile = ua.mobile;
    profil.ua_platform = ua.platform;
    try {
      const v = await ua.getHighEntropyValues?.(['platformVersion', 'architecture', 'bitness', 'model', 'fullVersionList', 'wow64', 'formFactors']);
      profil.st_hints = v ? 'ok' : 'unsupported';
      if (v) {
        if (typeof v.wow64 === 'boolean') profil.ua_wow64 = v.wow64;
        if (Array.isArray(v.formFactors) && v.formFactors.length) profil.ua_form_factors = v.formFactors.join(', ');
        if (typeof v.platformVersion === 'string') profil.ua_platform_version = v.platformVersion;
        if (typeof v.architecture === 'string') profil.ua_arch = v.architecture;
        if (typeof v.bitness === 'string') profil.ua_bitness = v.bitness;
        if (typeof v.model === 'string' && v.model) profil.ua_model = v.model;
        const polnye = v.fullVersionList as { brand: string; version: string }[] | undefined;
        if (Array.isArray(polnye) && polnye.length) profil.ua_full_versions = polnye.map((b) => `${b.brand} ${b.version}`).join(', ');
        const osnovnoy = polnye?.find((b) => /Chrome|Edge|Opera|Yandex/i.test(b.brand) && !/Not/i.test(b.brand));
        if (osnovnoy) profil.browser_version = osnovnoy.version;
      }
    } catch (e) {
      /* не поддержано или отказано: данных нет */
      profil.st_hints = poOshibke(e);
    }
  } else {
    profil.st_hints = 'unsupported';
  }

  for (const k of Object.keys(profil)) if (profil[k] === undefined || profil[k] === null || profil[k] === '') delete profil[k];
  return profil;
}

function xsrf(): string | null {
  const m = proba(() => document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/));
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Запись наблюдения на сервер. До входа у браузера ещё нет ключа защиты форм —
 * берём его тем же штатным адресом, что и форма входа. Ошибка молчит.
 */
async function zapisat(put: string, telo: unknown): Promise<void> {
  try {
    if (!xsrf()) await fetch(`${ROOT}/sanctum/csrf-cookie`, { credentials: 'include' });
    const klyuch = xsrf();
    await fetch(`${API_BASE}${put}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(klyuch ? { 'X-XSRF-TOKEN': klyuch } : {}),
        ...browserHeaders(),
      },
      body: JSON.stringify(telo),
    });
  } catch {
    /* наблюдение не обязано состояться */
  }
}

let otpravlen = '';

/**
 * Отправить профиль, если он изменился с прошлой отправки в этой вкладке.
 * Зовётся при открытии панели и страниц входа; повторные вызовы дёшевы.
 */
export async function otpravitProfil(): Promise<void> {
  try {
    const zagolovki = browserHeaders();
    if (!zagolovki['X-Opsmind-Browser']) return;
    const profil = await sobratProfil();
    const slepok = JSON.stringify(profil);
    if (slepok === otpravlen) return;
    otpravlen = slepok;
    // Время на устройстве — в момент отправки: сервер сравнит его со своим и сохранит
    // только расхождение (часы устройства бывают сбиты, и журнал ходьбы пишет по ним).
    await zapisat('/auth/browser-profile', { profile: { ...profil, client_time_ms: Date.now() } });
  } catch {
    /* наблюдение не обязано состояться */
  }
}

/** Страницу входа или приглашения открыли / тронули рукой — событие из закрытого списка. */
export function soobshchit(event: 'page.opened' | 'human.interaction', screen: 'login' | 'invitation' | 'password_recovery'): void {
  void zapisat('/auth/browser-events', { event, screen });
}

/**
 * Следить за страницей входа/приглашения: «открыта» сразу, «тронута рукой» — по
 * первому НАСТОЯЩЕМУ нажатию (isTrusted). Что именно нажато и введено, не читается.
 */
export function nablyudatStranitsu(screen: 'login' | 'invitation' | 'password_recovery', sOtkrytiem = true): () => void {
  if (sOtkrytiem) soobshchit('page.opened', screen);
  void otpravitProfil();
  let tronuta = false;
  const tronul = (e: Event) => {
    if (!e.isTrusted || tronuta) return;
    tronuta = true;
    soobshchit('human.interaction', screen);
  };
  window.addEventListener('pointerdown', tronul, { passive: true });
  window.addEventListener('keydown', tronul, { passive: true });
  return () => {
    window.removeEventListener('pointerdown', tronul);
    window.removeEventListener('keydown', tronul);
  };
}
